import type {
  FormulaError,
  ProductComputation,
  ProductSpec,
  VariantComputation,
} from '../types/product';

/**
 * Formule-engine voor uren-regels binnen één product.
 *
 * Formules verwijzen naar de `code` van andere regels in hetzelfde product en
 * nooit naar celposities. Dat is bewust: in de bronsheet verwezen S188 en S146
 * naar het tarief van een ánder blok, wat pas zichtbaar fout gaat zodra er één
 * tarief wijzigt. Met namen kan een regel niet stil naar iets anders gaan
 * wijzen, en een onbekende verwijzing is meteen een harde fout.
 *
 * Ondersteund: + - * / ( ), percentages (10%), en de functies
 * SUM, MIN, MAX, ROUND, CEIL, FLOOR, ABS.
 */

export { evaluateVariantHours, isFormula, validateFormula, FormulaSyntaxError } from './productHours';
import { evaluateVariantHours } from './productHours';

const roundCents = (value: number): number => Math.round(value);

/**
 * Rekent een volledig product door met de meegegeven tarieven.
 * `ratesByKey` bevat centen per uur, zoals ze golden op het peilmoment.
 */
export function computeProduct(
  spec: ProductSpec,
  ratesByKey: Record<string, number>,
  options: { computedAt?: Date; componentPriceCents?: Record<string, number> } = {},
): ProductComputation {
  const errors: FormulaError[] = [];
  const variants: VariantComputation[] = [];
  const ratesUsed: Record<string, number> = {};

  const variantList = spec.variants?.length ? spec.variants : [{ id: 'default', name: 'Standaard' }];

  for (const variant of variantList) {
    const variantErrors: FormulaError[] = [];
    const lineTotalCents: Record<string, number> = {};
    let hoursByCode: Record<string, number> = {};
    let totalHours = 0;
    let priceCents: number | null = null;

    if (spec.priceMode === 'hours') {
      const evaluated = evaluateVariantHours(spec.lines ?? [], variant.id);
      hoursByCode = evaluated.hoursByCode;
      variantErrors.push(...evaluated.errors);

      let sum = 0;
      for (const line of spec.lines ?? []) {
        const hours = hoursByCode[line.code] ?? 0;
        const rate = ratesByKey[line.rateKey];
        if (rate === undefined) {
          variantErrors.push({
            code: line.code,
            variantId: variant.id,
            message: `Onbekend tarief "${line.rateKey}"`,
          });
          lineTotalCents[line.code] = 0;
          continue;
        }
        ratesUsed[line.rateKey] = rate;
        const amount = roundCents(hours * rate);
        lineTotalCents[line.code] = amount;
        sum += amount;
        totalHours += hours;
      }
      priceCents = sum;
    } else if (spec.priceMode === 'fixed') {
      const fixed = spec.fixedPriceCents?.[variant.id];
      if (fixed === undefined) {
        variantErrors.push({
          variantId: variant.id,
          message: 'Geen vaste prijs ingevuld voor deze variant',
        });
        priceCents = 0;
      } else {
        priceCents = fixed;
      }
    } else {
      // on_request: bewust geen bedrag. Sales vult het per offerte in.
      priceCents = null;
    }

    // Bundelonderdelen tellen op bij de prijs wanneer die niet handmatig is gezet.
    if (spec.components?.length && options.componentPriceCents) {
      let componentTotal = 0;
      for (const component of spec.components) {
        const unit = options.componentPriceCents[component.productVersionId];
        if (unit === undefined) {
          variantErrors.push({
            variantId: variant.id,
            message: `Onderdeel ${component.productId} kon niet doorgerekend worden`,
          });
          continue;
        }
        componentTotal += roundCents(unit * (component.quantity || 1));
      }
      if (spec.priceMode === 'hours') {
        priceCents = (priceCents ?? 0) + componentTotal;
      }
    }

    let costCents: number | null = null;
    if (spec.cost?.mode === 'amount') {
      costCents = spec.cost.amountCentsByVariant?.[variant.id] ?? null;
    } else if (spec.cost?.mode === 'markup') {
      const base = spec.cost.amountCentsByVariant?.[variant.id];
      if (base !== undefined) {
        costCents = base;
        const pct = spec.cost.markupPct ?? 0;
        priceCents = roundCents(base * (1 + pct / 100));
      }
    }

    const marginCents =
      priceCents !== null && costCents !== null ? priceCents - costCents : null;
    const marginPct =
      marginCents !== null && priceCents !== null && priceCents !== 0
        ? Math.round((marginCents / priceCents) * 1000) / 10
        : null;

    variants.push({
      variantId: variant.id,
      hoursByCode,
      totalHours: Math.round(totalHours * 100) / 100,
      lineTotalCents,
      priceCents,
      costCents,
      marginCents,
      marginPct,
      errors: variantErrors,
    });
  }

  return {
    variants,
    ratesUsed,
    computedAt: (options.computedAt ?? new Date()).toISOString(),
    errors,
  };
}
